import { AnyAction, createAsyncThunk, createSlice, ThunkDispatch } from '@reduxjs/toolkit';
import { ISummaryResultList, TimeTableEventTabType } from '@App/types';
import { RootState } from '@App/store';

import summaryResultListApi from '@App/services/summaryResultList/summaryResultListApi';
import { setActiveTab } from '@Features/timeTableSelectedEvent/timeTableSelectedEventSlice';
import { setSelectedTimeTableId } from '@Features/timeTable/timeTableSlice';
import { eventsApi } from '@App/services/events/eventsApi';
import { pollingSucceeded, pollingFailed } from '@Features/errorHandler/polling/pollingErrorHandlerSlice';

import { pollingErrorHandlerSlice } from '@Features/errorHandler/polling/pollingErrorHandlerSlice';

export interface IGetResultListDataPayload {
  timeTableId: number;
}

export interface ISummaryResultListState {
  changeTimestamp: number; // Tohle trigeruje mount/unmout pooling kontejneru, tak aby se forsnul reload při znovu kliku (jinak se vysypal state)
  dataLoaded: boolean;
  isFetching: boolean;
  error: any;
  data: ISummaryResultList | null;
}

const initialState: ISummaryResultListState = {
  changeTimestamp: 0,
  dataLoaded: false,
  isFetching: false,
  error: null,
  data: null,
};

export const getSummaryResultListDataAsync = createAsyncThunk(
  'summaryResultList/fetchData',
  async ({ timeTableId }: IGetResultListDataPayload, { dispatch, rejectWithValue, signal }) => {
    try {
      const response = await summaryResultListApi.fetchSummaryResultListData(timeTableId, signal);

      const thunkDispatch = dispatch as ThunkDispatch<any, any, AnyAction>; // Nutno to přetypovat, TypeScript si s tím nějak
      thunkDispatch(eventsApi.util.updateQueryData('getEventsStates', '', draft => {
        draft.states[timeTableId] = response.eventState;
      }));

      dispatch(pollingSucceeded());

      return response.data;
    } catch (err) {
      dispatch(pollingFailed({ sectionName: 'SummaryResultList', error: err }));
      return rejectWithValue(err);
    }
  }
);

export const summaryResultListSlice = createSlice({
  name: 'summaryResultListSlice',
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSummaryResultListDataAsync.pending, state => {
        state.isFetching = true;
      })
      .addCase(getSummaryResultListDataAsync.fulfilled, (state, action) => {
        state.isFetching = false;
        state.dataLoaded = true;
        state.error = null;
        state.data = action.payload;
      })
      .addCase(getSummaryResultListDataAsync.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.isFetching = false;
          state.error = action.payload;
        }
      })
      .addCase(setActiveTab, (state, action) => { // Clear state
        if (action.payload === TimeTableEventTabType.SummaryResults) {
          state.changeTimestamp = Date.now();
        } else {
          Object.assign(state, initialState);
        }
      })
      .addCase(setSelectedTimeTableId, (state, action) => {
        if (action.payload.hasChanged) {
          Object.assign(state, initialState);
        } else {
          state.changeTimestamp = Date.now();
        }
      })
      .addCase(eventsApi.internalActions.unsubscribeQueryResult, state => { // Clear state when movind to different screen
        Object.assign(state, initialState);
      })
      .addCase(pollingErrorHandlerSlice.actions.clearPollingErrors, (state) => {
        state.error = null;
      });
  }
});

export const selectIsFetching = (state: RootState) => state.summaryResultList.isFetching;
export const selectError = (state: RootState) => state.summaryResultList.error;
export const selectDataLoaded = (state: RootState) => state.summaryResultList.dataLoaded;
export const selectData = (state: RootState) => state.summaryResultList.data;
export const selectChangeTimestamp = (state: RootState) => state.summaryResultList.changeTimestamp;

export default summaryResultListSlice.reducer;